import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createCashDepositLink, getWidgetApiError, isDeadLinkError } from "../services/api";
import { APP_ROUTES } from "../constants";
import {
  appendAnalyticsHandoff,
  getAnalyticsSession,
  getEvents,
  safeCapture,
} from "../analytics";

/**
 * Opens the cash-deposit widget for an awaiting-funding operation by minting
 * an operation_payment magic link and doing a full navigation.
 */
export function useLaunchCashDepositPayment(linkId: string | null | undefined) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [launching, setLaunching] = useState(false);

  const launch = useCallback(
    async (operationId: string) => {
      if (!linkId || launching) return;
      setLaunching(true);
      try {
        const callbackUrl = `${window.location.origin}${APP_ROUTES.HOME_PAGE}/?linkId=${linkId}`;
        const { url } = await createCashDepositLink(
          linkId,
          operationId,
          callbackUrl,
        );
        const { sessionId, distinctId } = getAnalyticsSession();
        const handoffUrl =
          sessionId && distinctId
            ? appendAnalyticsHandoff(url, { sessionId, distinctId })
            : url;
        window.location.assign(handoffUrl);
      } catch (error) {
        console.error("[useLaunchCashDepositPayment] Failed to mint link:", error);
        safeCapture(getEvents().BARCODE_CREATED, {
          success: false,
          error_code: "CASH_DEPOSIT_LINK_FAILED",
          operation_id: operationId,
        });
        setLaunching(false);
        if (isDeadLinkError(error)) return;
        const maintenance = destinationMaintenanceCopy(error);
        navigate(APP_ROUTES.ERROR_PAGE, {
          state: {
            title: maintenance?.title ?? null,
            subTitle: null,
            message: maintenance?.body ?? null,
            btnText: t("cashDeposit.back"),
            isOut: false,
            pageUrl: `${APP_ROUTES.HOME_PAGE}/?linkId=${linkId}`,
          },
        });
      }
    },
    [linkId, launching, navigate, t],
  );

  return { launch, launching };
}

function destinationMaintenanceCopy(
  error: unknown,
): { title: string; body: string } | null {
  const api = getWidgetApiError(error);
  if (api?.code !== "FLOW_UNAVAILABLE_MAINTENANCE") return null;
  const details = (api.details ?? {}) as { title?: string; body?: string };
  const title = details.title?.trim() ?? "";
  const body = details.body?.trim() ?? "";
  if (!title && !body) return null;
  return { title, body };
}
